import { COMPETITION_SETUP_ROLES, type RoleCode, VENUE_SORT_KEYS, type VenueItem, type VenueSortKey } from "@seagames/contracts";
import { type ColumnDef, DataTable, IconButton, Icons, ListHeader, ListToolbar, PrimaryCell, StatsStrip, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";
import { ConfirmDeleteModal } from "../../components/common/confirm-delete-modal";
import { EditDeleteActions } from "../../components/common/edit-delete-actions";
import { timestampColumn, usageColumn } from "../../components/common/list-columns";
import { CodeBadge } from "../../components/competition/code-badge";
import { ClusterCell } from "../../components/transport-fleet/hooks";
import { type VenueFormTarget, VenueFormModal } from "../../components/venues/venue-form-modal";
import { ApiError } from "../../lib/api/client";
import { requireAdminRole } from "../../lib/auth/guards";
import { listSearch } from "../../lib/list-page/list-search";
import { useConfirmDelete } from "../../lib/list-page/use-confirm-delete";
import { sortSelect } from "../../lib/list-page/sort-select";
import { useListPage } from "../../lib/list-page/use-list-page";
import { deleteVenue, invalidateVenues, venueOptionsOptions, venueStatsOptions, venuesListOptions } from "../../lib/venues/queries";

/**
 * Settings › Venues. The URL's `parent_venue_id` is the "Show" choice:
 * absent = top-level venues only (the default, sent to the API as `none`),
 * `all` = every venue and court (sent as no filter), a venue id = its courts.
 */
export const Route = createFileRoute("/_app/settings/venues")({
	beforeLoad: ({ context }) => requireAdminRole(context.session),
	validateSearch: listSearch(VENUE_SORT_KEYS, { parent_venue_id: z.union([z.literal("none"), z.literal("all"), z.string().uuid()]) }),
	component: VenuesPage,
});

/** The "Show" choice: `none` (top level, the default), `all`, or a venue id. */
const LEVEL_TOP = "none";
const LEVEL_ALL = "all";

interface VenueFilters extends Record<string, unknown> {
	search: string;
	level: string;
}
const EMPTY_FILTERS: VenueFilters = { search: "", level: LEVEL_TOP };

/** Turn a 409 CONFLICT into the page's own "in use" sentence; anything else passes through to the toast as is. */
const inUseAs = (message: string) => (err: unknown) => {
	throw err instanceof ApiError && err.code === "CONFLICT" ? new Error(message) : err;
};

function VenuesPage() {
	const { t } = useTranslation("venues");
	const { t: tc } = useTranslation("common");
	const { t: tf } = useTranslation("transportFleet");
	const { session } = Route.useRouteContext();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const { formatNumber } = useUiStrings();
	const canWrite = session.active_role !== null && (COMPETITION_SETUP_ROLES as readonly RoleCode[]).includes(session.active_role);
	const [form, setForm] = useState<VenueFormTarget | null>(null);

	const level = search.parent_venue_id ?? LEVEL_TOP;
	/** The venue whose courts are listed, when the filter names one. */
	const insideId = level !== LEVEL_TOP && level !== LEVEL_ALL ? level : undefined;

	// The "Courts inside" menu's venues (the pickers' cache; top-level ones are its entries).
	const venueOptions = useQuery(venueOptionsOptions());
	const topLevel = useMemo(() => (venueOptions.data ?? []).filter((v) => v.parent_venue_id === null), [venueOptions.data]);
	const inside = insideId !== undefined ? topLevel.find((v) => v.venue_id === insideId) : undefined;
	// The strip and the level chips' counts — one aggregate statement, unfiltered.
	const stats = useQuery(venueStatsOptions());

	const page = useListPage({
		search,
		navigate,
		listOptions: (q) => venuesListOptions({ ...q, parent_venue_id: level === LEVEL_ALL ? undefined : level }),
		columnsKey: "venues",
		filters: {
			empty: EMPTY_FILTERS,
			fromUrl: (s) => ({ search: s.search ?? "", level: s.parent_venue_id ?? LEVEL_TOP }),
			toUrl: (f) => ({
				search: f.search.trim() || undefined,
				parent_venue_id: f.level === LEVEL_TOP || f.level === "" ? undefined : (f.level as typeof search.parent_venue_id),
			}),
		},
		summary: { range: (v) => t("pagination.summary", v), empty: t("pagination.empty") },
	});
	const del = useConfirmDelete<VenueItem>({
		// 409 = still has courts or meets; the API sends no details, so the copy is ours.
		remove: (r) => deleteVenue(r.venue_id, `Deleted venue ${r.code}`).catch(inUseAs(t("errors.inUse"))),
		invalidate: invalidateVenues,
		deleted: (r) => t("delete.deleted", { name: r.name }),
	});

	const total = page.list.data?.total;
	const sort = sortSelect<VenueSortKey>(
		search,
		"code:asc",
		[
			{ value: "code:asc", label: tc("sort.codeAsc") },
			{ value: "code:desc", label: tc("sort.codeDesc") },
			{ value: "name:asc", label: tc("sort.nameAsc") },
			{ value: "name:desc", label: tc("sort.nameDesc") },
			{ value: "city:asc", label: t("sort.cityAsc") },
			{ value: "city:desc", label: t("sort.cityDesc") },
			{ value: "created_at:desc", label: tc("sort.newest") },
			{ value: "created_at:asc", label: tc("sort.oldest") },
		],
		page.patch,
	);

	const columns: ColumnDef<VenueItem, VenueSortKey>[] = [
		{ key: "name", header: t("table.name"), sortable: true, lockVisible: true, minWidth: "10.5rem", cell: (r) => <PrimaryCell sub={r.code}>{r.name}</PrimaryCell> },
		{
			key: "city",
			header: t("table.city"),
			sortable: true,
			minWidth: "6rem",
			cell: (r) => (r.city !== null ? <span>{r.city}</span> : <span className="text-base-content/30">—</span>),
		},
		{
			key: "parent" as VenueSortKey,
			header: <span title={t("table.parentTitle")}>{t("table.parent")}</span>,
			// Compact (E2E B2: the table must fit 1440 px with every column): the code, the name wrapping under it.
			minWidth: "8rem",
			cell: (r) =>
				r.parent_venue_id !== null ? (
					<span className="grid gap-[0.125rem]" title={`${r.parent_code ?? ""} ${r.parent_name ?? ""}`.trim()}>
						<CodeBadge code={r.parent_code ?? ""} className="text-gms-ink-3" />
						<span className="text-[0.8125rem] leading-snug">{r.parent_name}</span>
					</span>
				) : (
					<span className="text-base-content/30">—</span>
				),
		},
		{
			key: "children_count" as VenueSortKey,
			header: t("table.children"),
			width: "6rem",
			numeric: true,
			cell: (r) =>
				r.children_count > 0 ? (
					<Link
						to="/settings/venues"
						search={{ parent_venue_id: r.venue_id }}
						className="font-semibold tabular-nums text-gms-navy hover:underline"
						title={t("table.childrenTitle")}
					>
						{formatNumber(r.children_count)}
					</Link>
				) : (
					<span className="text-base-content/30">{formatNumber(0)}</span>
				),
		},
		{
			// M10: the transport cluster (drive times, dispatch). None = not offered as a trip place.
			key: "cluster" as VenueSortKey,
			header: tf("clusterField.column"),
			minWidth: "5rem",
			cell: (r) => (r.cluster ? <ClusterCell cluster={r.cluster} /> : <span className="text-gms-ink-3">{tf("clusterField.none")}</span>),
		},
		usageColumn("meets_count", t("table.meets"), t("table.meetsTitle")),
		timestampColumn("created_at", t("table.created"), true),
		timestampColumn("updated_at", t("table.updated")),
	];

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			<ListHeader
				title={t("page.title")}
				count={total === undefined ? undefined : t("page.count", { count: total, n: formatNumber(total) })}
				description={<Trans t={t} i18nKey="page.intro" components={{ strong: <strong className="font-semibold text-gms-ink-2" /> }} />}
				// While one venue's courts are listed, "Add" means a court of that venue.
				actionLabel={inside ? t("page.addCourt", { name: inside.name }) : t("page.addVenue")}
				onAction={canWrite ? () => setForm({ mode: "create", parentVenueId: insideId }) : undefined}
			/>

			<StatsStrip
				label={tc("list.summary")}
				cells={[
					{ key: "total", label: t("stats.total"), value: total ?? "—", note: search.search !== undefined ? t("stats.matching") : t(`stats.view.${inside ? "inside" : level === LEVEL_ALL ? "all" : "top"}`) },
					...(stats.data
						? [
								{ key: "venues", label: t("stats.venues"), value: stats.data.top_level },
								{ key: "courts", label: t("stats.courts"), value: stats.data.courts },
								{ key: "with-courts", label: t("stats.withCourts"), value: stats.data.with_courts },
							]
						: []),
				]}
			/>

			<ListToolbar
				search={search.search ?? ""}
				onSearch={(v) => page.patch({ search: v || undefined })}
				searchPlaceholder={t("filters.searchPlaceholder")}
				sort={sort}
				chips={[
					{
						kind: "options",
						key: "level",
						label: t("filters.levelLabel"),
						options: [
							{ value: LEVEL_TOP, label: t("filters.topLevelOnly"), count: stats.data?.top_level },
							{ value: LEVEL_ALL, label: t("filters.allVenues"), count: stats.data?.total },
						],
						value: insideId === undefined ? [level] : [],
						// The top level is the default view, so un-pressing a chip falls back to it.
						onChange: (v) => page.patch({ parent_venue_id: v[0] === LEVEL_ALL ? LEVEL_ALL : undefined }),
					},
					{
						kind: "menu",
						key: "inside",
						label: t("filters.insideLabel"),
						options: topLevel.map((v) => ({ value: v.venue_id, label: v.name })),
						value: insideId ?? null,
						onChange: (v) => page.patch({ parent_venue_id: v ?? undefined }),
						searchPlaceholder: t("filters.searchVenues"),
						disabled: venueOptions.isPending,
					},
				]}
				onClearAll={page.clearFilters}
			/>

			<DataTable<VenueItem, VenueSortKey>
				{...page.table}
				variant="list"
				columns={columns}
				rowKey={(r) => r.venue_id}
				empty={
					inside && !page.list.data?.items.length && !search.search
						? { title: t("empty.childrenNoneTitle"), description: t("empty.childrenNoneDescription", { name: inside.name }) }
						: page.emptyState({
								filtered: { title: t("empty.filteredTitle"), description: t("empty.filteredDescription") },
								none: { title: t("empty.noneTitle"), description: t("empty.noneDescription") },
								clearLabel: t("empty.clearFilters"),
							})
				}
				rowActions={(r) => (
					<EditDeleteActions
						editLabel={t("table.editLabel", { name: r.name })}
						onEdit={canWrite ? () => setForm({ mode: "edit", venue: r }) : undefined}
						deleteLabel={t("table.deleteLabel", { name: r.name })}
						onDelete={canWrite ? () => del.ask(r) : undefined}
						canDelete={r.children_count === 0 && r.meets_count === 0}
						variant="list"
					>
						{r.children_count > 0 && (
							<IconButton
								label={t("table.viewChildrenLabel", { name: r.name })}
								icon={<Icons.Eye />}
								size="xs"
								variant="row"
								onClick={() => void navigate({ search: (prev) => ({ ...prev, page: undefined, parent_venue_id: r.venue_id }) })}
							/>
						)}
						{/* Courts nest one level: only a top-level venue can take one. */}
						{canWrite && r.parent_venue_id === null && (
							<IconButton
								label={t("table.addChildLabel", { name: r.name })}
								icon={<Icons.Plus />}
								size="xs"
								variant="row"
								onClick={() => setForm({ mode: "create", parentVenueId: r.venue_id })}
							/>
						)}
					</EditDeleteActions>
				)}
			/>

			{canWrite && <VenueFormModal target={form} onClose={() => setForm(null)} />}
			{canWrite && (
				<ConfirmDeleteModal
					{...del.modal}
					title={t("delete.title")}
					description={<Trans t={t} i18nKey="delete.description" values={{ name: del.row?.name ?? "" }} components={{ strong: <strong className="font-semibold text-base-content" /> }} />}
					confirmLabel={t("delete.confirm")}
					errorPrefix={t("delete.failed")}
				/>
			)}
		</div>
	);
}
